import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Brain, Globe, Wrench, BookOpen, Cog, Check, Copy, RefreshCw, type LucideIcon,
} from 'lucide-react';
import { RichMarkdown } from './render/RichMarkdown';
import { ToolCallCard, KnowledgeBaseRow } from './ToolCallCard';
import { ToolApprovalCard } from './ToolApprovalCard';
import { JobPlanCard } from './JobPlanCard';
import { ArtifactMiniCard } from './ArtifactMiniCard';
import { ReasoningBlock } from '../ReasoningBlock';
import { buildPaWelcome } from '../paWelcome';
import { isWebSearchTool } from '../paToolDisplay';
import { formatRelativeTime, stripWorkspaceArtifacts } from './chatFormat';
import { PaFace, type PaFaceState } from '../avatar/PaFace';
import type { PendingApproval, WorkspaceUiArtifact } from './types';
import type { PaToolBadge, PaRagMeta } from '../paStreamReducer';
import type { PaAvatarConfig, PaJob } from '@easyflow/shared';

export interface ChatStreamMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: PaToolBadge[];
  rag?: PaRagMeta;
  created_at: string;
}

export interface ChatMessageStreamProps {
  assistantName: string;
  assistantColor: string;
  assistantDescription?: string | null;
  userFirstName?: string | null;
  AssistantIcon: React.ComponentType<{ className?: string }>;

  messages: ChatStreamMessage[];
  streamingContent: string;
  streamingThinking: string;
  activeTools: PaToolBadge[];
  activeRag?: PaRagMeta;
  isStreaming: boolean;
  streamingStatus: string;

  /** Artefatti della sola conversazione corrente, per le mini-card inline. */
  conversationArtifacts: WorkspaceUiArtifact[];
  approvals: PendingApproval[];
  onDecideApproval: (id: string, decision: 'approved' | 'rejected', notes?: string, overrideArgs?: Record<string, unknown>) => Promise<void>;
  onOpenArtifact: (id: string) => void;

  conversationJobs?: PaJob[];
  jobsLoading?: boolean;
  jobsError?: string | null;
  onRetryJobs?: () => void | Promise<void>;
  onDecideJob?: (id: string, decision: 'approved' | 'rejected', note: string | undefined, expectedVersion: number) => Promise<void>;
  onCancelJob?: (id: string) => Promise<void>;

  /** Gutter + spaziatura del contenitore (default = colonna desktop). */
  containerClassName?: string;
  /** Larghezza del blocco assistente (default = full-width desktop). */
  assistantBlockClass?: string;
  /** Rigenera l'ultima risposta (toolbar hover sull'ultimo messaggio assistant). */
  onRegenerate?: () => void;

  // Task #3041 — avatar personale: quando abilitato compare accanto ai
  // messaggi dell'assistente e nel blocco streaming (con stato live).
  avatarEnabled?: boolean;
  avatarConfig?: PaAvatarConfig | null;
  avatarDefaultSeed?: string;
  faceState?: PaFaceState;
}

// La tipografia vive in RichMarkdown, condivisa anche dai documenti.
// Stessa larghezza per storico e streaming: il flip live→storico non salta.
const PROSE_CLASS = 'max-w-[70ch]';

// Task #3817 — riga azioni sotto ogni messaggio assistant: timestamp sempre
// visibile, azioni (copia, rigenera sull'ultimo) emergono in hover senza
// spostare il layout (riga a altezza fissa). Estratta per riuso nei due rami
// avatar ON/OFF.
const MessageActionsRow: React.FC<{
  messageId: string;
  createdAt: string;
  content: string;
  copiedId: string | null;
  onCopy: (id: string, text: string) => void;
  onRegenerate?: () => void;
}> = ({ messageId, createdAt, content, copiedId, onCopy, onRegenerate }) => (
  <div className="mt-1 flex h-5 items-center gap-1.5">
    <span className="text-[10px] text-muted-foreground">{formatRelativeTime(createdAt)}</span>
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150">
      <button
        type="button"
        title="Copia risposta"
        aria-label="Copia risposta"
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        onClick={() => onCopy(messageId, stripWorkspaceArtifacts(content))}
      >
        {copiedId === messageId ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
        {copiedId === messageId ? 'Copiato' : 'Copia'}
      </button>
      {onRegenerate && (
        <button
          type="button"
          title="Rigenera risposta"
          aria-label="Rigenera risposta"
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          onClick={onRegenerate}
        >
          <RefreshCw className="h-3 w-3" /> Rigenera
        </button>
      )}
    </div>
  </div>
);

// Stream conversazione condiviso (single source of truth): bolle utente/assistant,
// markdown, mini-card artefatti ancorate per messageId + orfani, blocco streaming
// (reasoning + markdown live), feed live contestuale unico, approvazioni inline.
// Desktop e mobile montano QUESTO componente; cambia solo il frame (gutter/scroll).
export const ChatMessageStream: React.FC<ChatMessageStreamProps> = ({
  assistantName, assistantColor, assistantDescription, userFirstName, AssistantIcon,
  messages, streamingContent, streamingThinking, activeTools, activeRag, isStreaming, streamingStatus,
  conversationArtifacts, approvals, onDecideApproval, onOpenArtifact,
  conversationJobs = [], jobsLoading = false, jobsError = null, onRetryJobs, onDecideJob, onCancelJob,
  containerClassName = 'px-[1cm] py-3 space-y-3 w-full',
  assistantBlockClass = 'w-full',
  onRegenerate,
  avatarEnabled = false,
  avatarConfig = null,
  avatarDefaultSeed = 'pa',
  faceState = 'idle',
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);

  useEffect(() => () => { if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current); }, []);

  const copyMessage = (id: string, text: string): void => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  };

  useEffect(() => {
    // Durante lo streaming lo scroll segue i flush (~30fps): 'auto' evita le
    // animazioni smooth accavallate che fanno "rimbalzare" il layout.
    messagesEndRef.current?.scrollIntoView({ behavior: isStreaming ? 'auto' : 'smooth', block: 'end' });
  }, [messages, streamingContent, streamingThinking, activeTools.length, isStreaming]);

  const { artifactsByMessage, orphanArtifacts, lastAssistantId } = useMemo(() => {
    const messageIds = new Set(messages.map(m => m.id));
    const byMessage = new Map<string, WorkspaceUiArtifact[]>();
    const orphans: WorkspaceUiArtifact[] = [];
    for (const a of conversationArtifacts) {
      if (a.messageId && messageIds.has(a.messageId)) {
        const list = byMessage.get(a.messageId) ?? [];
        list.push(a);
        byMessage.set(a.messageId, list);
      } else {
        orphans.push(a);
      }
    }
    let lastAsst: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg && msg.role === 'assistant') {
        lastAsst = msg.id;
        break;
      }
    }
    return { artifactsByMessage: byMessage, orphanArtifacts: orphans, lastAssistantId: lastAsst };
  }, [conversationArtifacts, messages]);

  return (
    <div className={containerClassName}>
      {messages.length === 0 && !streamingContent && (() => {
        const welcome = buildPaWelcome({ assistantName, assistantDescription, userFirstName });
        return (
          <div className="flex flex-col items-center text-center py-12 px-4">
            {/* Task #3041 — faccia del PA: icona (OFF, markup identico a prima) o avatar. */}
            <PaFace
              avatarEnabled={avatarEnabled}
              avatarConfig={avatarConfig}
              defaultSeed={avatarDefaultSeed}
              color={assistantColor}
              FallbackIcon={AssistantIcon}
              size={56}
              rounded="rounded-2xl"
              iconClassName="h-7 w-7"
              className="mb-4"
              showStateLayer={false}
            />
            <h3 className="font-semibold text-foreground mb-1 text-lg">{welcome.heading}</h3>
            <p className="text-sm font-medium text-foreground mb-1 max-w-md">{welcome.intro}</p>
            <p className="text-sm text-muted-foreground max-w-md">{welcome.body}</p>
          </div>
        );
      })()}
      {messages.map(m => {
        const msgArtifacts = artifactsByMessage.get(m.id);
        // Artefatti orfani (turno live pre-persistenza il cui messageId
        // non si è ancora/mai riconciliato) ancorati all'ultimo messaggio
        // assistant a turno concluso: così scorrono col flusso chat invece
        // di restare inchiodati in fondo alla conversazione.
        const anchoredArtifacts =
          !isStreaming && m.role === 'assistant' && m.id === lastAssistantId
            ? [...(msgArtifacts ?? []), ...orphanArtifacts]
            : (msgArtifacts ?? []);
        if (m.role === 'user') {
          return (
            <React.Fragment key={m.id}>
              <div className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm bg-violet-600 text-white">
                  <span className="whitespace-pre-wrap">{m.content}</span>
                  <div className="text-[10px] mt-1 text-white/70">{formatRelativeTime(m.created_at)}</div>
                </div>
              </div>
              {msgArtifacts && msgArtifacts.length > 0 && (
                <div className="flex flex-col items-start gap-1.5">
                  {msgArtifacts.map(a => (
                    <ArtifactMiniCard key={a.id} artifact={a} onOpen={onOpenArtifact} />
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={m.id}>
            {(m.rag || (m.tool_calls && m.tool_calls.length > 0)) && (
              <div className={`flex w-full flex-col items-start gap-1.5 ${assistantBlockClass}`}>
                {m.rag && <KnowledgeBaseRow rag={m.rag} />}
                {m.tool_calls?.map((tc, i) => (
                  <ToolCallCard key={i} tool={tc} />
                ))}
              </div>
            )}
            <div className={`w-full group ${assistantBlockClass}`}>
              {/* Task #3041 — ad avatar attivo, la faccia compare accanto al
                  messaggio (stato spento: è storico); a OFF markup invariato.
                  Task #3817 — markdown ricco + toolbar hover (copia/rigenera)
                  su riga a altezza fissa: nessun salto di layout. */}
              {avatarEnabled ? (
                <div className="flex items-start gap-2">
                  <PaFace
                    avatarEnabled
                    avatarConfig={avatarConfig}
                    defaultSeed={avatarDefaultSeed}
                    color={assistantColor}
                    FallbackIcon={AssistantIcon}
                    size={24}
                    className="mt-1"
                    showStateLayer={false}
                  />
                  <div className="min-w-0 flex-1">
                    <RichMarkdown breaks className={PROSE_CLASS}>{stripWorkspaceArtifacts(m.content)}</RichMarkdown>
                    <MessageActionsRow
                      messageId={m.id}
                      createdAt={m.created_at}
                      content={m.content}
                      copiedId={copiedId}
                      onCopy={copyMessage}
                      onRegenerate={onRegenerate && !isStreaming && m.id === lastAssistantId ? onRegenerate : undefined}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <RichMarkdown breaks className={PROSE_CLASS}>{stripWorkspaceArtifacts(m.content)}</RichMarkdown>
                  <MessageActionsRow
                    messageId={m.id}
                    createdAt={m.created_at}
                    content={m.content}
                    copiedId={copiedId}
                    onCopy={copyMessage}
                    onRegenerate={onRegenerate && !isStreaming && m.id === lastAssistantId ? onRegenerate : undefined}
                  />
                </>
              )}
            </div>
            {anchoredArtifacts.length > 0 && (
              <div className="flex flex-col items-start gap-1.5">
                {anchoredArtifacts.map(a => (
                  <ArtifactMiniCard key={a.id} artifact={a} onOpen={onOpenArtifact} />
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
      {isStreaming && (activeTools.length > 0 || activeRag) && (
        <div className={`flex w-full flex-col items-start gap-1.5 ${assistantBlockClass}`}>
          {activeRag && <KnowledgeBaseRow rag={activeRag} />}
          {activeTools.map((t, i) => (
            <ToolCallCard key={i} tool={t} />
          ))}
        </div>
      )}

      {isStreaming && (streamingThinking || streamingContent) && (
        <div className={`w-full ${assistantBlockClass}`}>
          {/* Task #3041 — ad avatar attivo, faccia con stato LIVE accanto al blocco streaming. */}
          {avatarEnabled ? (
            <div className="flex items-start gap-2">
              <PaFace
                avatarEnabled
                avatarConfig={avatarConfig}
                defaultSeed={avatarDefaultSeed}
                color={assistantColor}
                FallbackIcon={AssistantIcon}
                state={faceState}
                size={24}
                className="mt-1"
              />
              <div className="min-w-0 flex-1">
                {streamingThinking && (
                  <ReasoningBlock thinking={streamingThinking} live={!streamingContent} />
                )}
                {streamingContent && (
                  <RichMarkdown breaks className={PROSE_CLASS}>
                    {stripWorkspaceArtifacts(streamingContent, true)}
                  </RichMarkdown>
                )}
              </div>
            </div>
          ) : (
            <>
              {streamingThinking && (
                <ReasoningBlock thinking={streamingThinking} live={!streamingContent} />
              )}
              {streamingContent && (
                <RichMarkdown breaks className={PROSE_CLASS}>
                  {stripWorkspaceArtifacts(streamingContent, true)}
                </RichMarkdown>
              )}
            </>
          )}
        </div>
      )}

      {/* Feed live contestuale UNICO: deriva lo stato dai segnali del
          turno così l'utente vede SEMPRE cosa sta facendo l'agente
          (cerco web / eseguo tool / consulto KB / ragiono / scrivo)
          invece di un generico "Sto ragionando". */}
      {isStreaming && (() => {
        const running = activeTools.find(t => t.status === 'running');
        // I tool nativi del provider (es. web search Claude) arrivano via
        // `server_tool_use` già marcati 'success' (mai tra i `running`): senza
        // questo ramo il web search non emergerebbe come stato del feed.
        const webSearchActive = !streamingContent && activeTools.some(t => isWebSearchTool(t.name));
        // Stato canonico del turno: 5 voci con icona simbolica e colore
        // NEUTRO (nessun viola/brand). L'override `streamingStatus` resta
        // per i messaggi operativi del backend (rate-limit/retry).
        let label: string;
        let Icon: LucideIcon;
        let spin = false;
        if (streamingStatus) {
          label = streamingStatus; Icon = Loader2; spin = true;
        } else if (running && isWebSearchTool(running.name)) {
          label = 'Cerco sul web'; Icon = Globe;
        } else if (running) {
          label = 'Richiamo tool MCP'; Icon = Wrench;
        } else if (webSearchActive) {
          label = 'Cerco sul web'; Icon = Globe;
        } else if (activeRag && !streamingContent) {
          label = 'Cerco in KB'; Icon = BookOpen;
        } else if (streamingThinking && !streamingContent) {
          label = 'Sto ragionando'; Icon = Brain;
        } else if (streamingContent) {
          label = 'Sto lavorando'; Icon = Cog; spin = true;
        } else {
          label = 'Sto ragionando'; Icon = Brain;
        }
        return (
          <div className="flex items-center justify-start gap-2">
            {/* Task #3041 — ad avatar attivo la faccia accompagna il feed live (solo se non c'è già il blocco streaming sopra). */}
            {avatarEnabled && !streamingThinking && !streamingContent && (
              <PaFace
                avatarEnabled
                avatarConfig={avatarConfig}
                defaultSeed={avatarDefaultSeed}
                color={assistantColor}
                FallbackIcon={AssistantIcon}
                state={faceState}
                size={24}
              />
            )}
            <div
              key={label}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-muted border border-border text-muted-foreground text-xs font-medium animate-in fade-in-0 slide-in-from-left-1 duration-300"
            >
              <Icon className={`h-3.5 w-3.5 text-muted-foreground ${spin ? 'animate-spin' : 'animate-pulse'}`} />
              <span className="animate-in fade-in-0 duration-200">{label}</span>
            </div>
          </div>
        );
      })()}
      {(isStreaming || !lastAssistantId) && orphanArtifacts.length > 0 && (
        <div className="flex flex-col items-start gap-1.5">
          {orphanArtifacts.map(a => (
            <ArtifactMiniCard key={a.id} artifact={a} onOpen={onOpenArtifact} />
          ))}
        </div>
      )}
      {conversationJobs.length > 0 && onDecideJob && onCancelJob && (
        <div className="space-y-2">
          {conversationJobs.map(job => (
            <JobPlanCard key={job.id} job={job} onDecide={onDecideJob} onCancel={onCancelJob} />
          ))}
        </div>
      )}
      {jobsLoading && (
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Aggiornamento piani…
        </div>
      )}
      {jobsError && (
        <div className="flex max-w-[560px] items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
          <span className="text-xs text-danger">{jobsError}</span>
          {onRetryJobs && (
            <button
              type="button"
              className="shrink-0 rounded border border-danger/30 px-2 py-1 text-xs font-medium text-danger hover:bg-danger/10"
              onClick={() => { void onRetryJobs(); }}
            >
              Riprova
            </button>
          )}
        </div>
      )}
      {approvals.length > 0 && (
        <div className="space-y-2">
          {approvals.map(a => (
            <ToolApprovalCard key={a.id} approval={a} onDecide={(d, n, o) => onDecideApproval(a.id, d, n, o)} />
          ))}
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};
